import React, { useEffect } from 'react'
import { useForm, useFieldArray, Control, Controller } from 'react-hook-form'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import FbButton from 'components/FbUI/FbButton'
import { CiSquarePlus } from 'react-icons/ci'
import { MdClose } from 'react-icons/md'
import { ProductSelector } from 'components/UI/ProductSelector'
import { BrandSelector } from 'components/UI/BrandSelector'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { getSingularUnit } from 'utils/reportingUnits'

export interface FormVariant {
  id?: number
  name: string
  description?: string
  gtin?: string
  sku?: string
  format_pack_size?: string
  avg_price?: number | null
  product?: number | null
  brand?: number | null
  case_weight?: number
  case_weight_unit?: string
  unit_weight?: number
  unit_weight_unit?: string
  units_per_case?: number
  distributor_codes?: Array<{
    distributor_name: string
    distributor_code: string
  }>
}

interface VariantFormProps {
  onSave: (variant: FormVariant) => Promise<void>
  onCancel: () => void
  initialData?: FormVariant | null
  reportingUnit?: string
  isSyndigoVariant?: boolean
}

export const VariantForm: React.FC<VariantFormProps> = ({
  onSave,
  onCancel,
  initialData,
  reportingUnit = 'units',
  isSyndigoVariant,
}) => {
  const { enableBrandManagement } = useFeatureFlagService()
  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<{ variant: FormVariant }>({
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      variant: {
        name: '',
        description: '',
        gtin: '',
        sku: '',
        format_pack_size: '',
        avg_price: null,
        product: null,
        brand: null,
        case_weight_unit: reportingUnit,
        unit_weight_unit: reportingUnit,
        distributor_codes: [],
      },
    },
  })

  const {
    fields: distributorCodeFields,
    append: appendDistributorCode,
    remove: removeDistributorCode,
  } = useFieldArray({
    control,
    name: 'variant.distributor_codes',
  })

  const variant = watch('variant')

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({
        variant: {
          ...initialData,
          // Map product_id from API to product for form
          product:
            (initialData as any).product_id ?? initialData.product ?? null,
          // Map brand_id from API to brand for form
          brand: (initialData as any).brand_id ?? initialData.brand ?? null,
          distributor_codes: initialData.distributor_codes || [],
        },
      })
    } else {
      reset({
        variant: {
          name: '',
          description: '',
          gtin: '',
          sku: '',
          format_pack_size: '',
          avg_price: null,
          product: null,
          brand: null,
          case_weight_unit: reportingUnit,
          unit_weight_unit: reportingUnit,
          distributor_codes: [],
        },
      })
    }
  }, [initialData, reset, reportingUnit])

  const handleAddDistributorCode = () => {
    appendDistributorCode({
      distributor_name: '',
      distributor_code: '',
    })
  }

  const handleFormSubmit = async (
    data: { variant: FormVariant },
    event?: React.BaseSyntheticEvent
  ) => {
    // Prevent the event from bubbling to parent forms
    event?.preventDefault()
    event?.stopPropagation()

    // Validate distributor codes have both name and code
    const distributorCodes = data.variant.distributor_codes || []
    let hasIncompleteCode = false

    distributorCodes.forEach((code, index) => {
      const hasName = code.distributor_name?.trim()
      const hasCode = code.distributor_code?.trim()

      // If one is filled but not the other, show error
      if (hasName && !hasCode) {
        setError(`variant.distributor_codes.${index}.distributor_code`, {
          type: 'manual',
          message: 'Distributor code is required',
        })
        hasIncompleteCode = true
      }
      if (hasCode && !hasName) {
        setError(`variant.distributor_codes.${index}.distributor_name`, {
          type: 'manual',
          message: 'Distributor name is required',
        })
        hasIncompleteCode = true
      }
    })

    if (hasIncompleteCode) {
      return
    }

    // Validate no duplicate distributor names (only for filled entries)
    const filledDistributorCodes = distributorCodes.filter(
      (code) => code.distributor_name?.trim() && code.distributor_code?.trim()
    )
    const distributorNames = filledDistributorCodes
      .map((code) => code.distributor_name?.trim().toLowerCase())
      .filter((name) => name)

    const duplicates = distributorNames.filter(
      (name, index) => distributorNames.indexOf(name) !== index
    )

    if (duplicates.length > 0) {
      // Find the first index of the duplicate name in the original array
      const duplicateName = duplicates[0]
      const duplicateIndex = distributorCodes.findIndex(
        (code) => code.distributor_name?.trim().toLowerCase() === duplicateName
      )

      // Set error on the duplicate field
      if (duplicateIndex !== -1) {
        setError(
          `variant.distributor_codes.${duplicateIndex}.distributor_name`,
          {
            type: 'manual',
            message: 'Distributor name must be unique',
          }
        )
      }
      return // Don't submit if there are duplicates
    }

    // Filter out empty distributor codes before saving
    const cleanedVariant = {
      ...data.variant,
      distributor_codes: filledDistributorCodes,
    }

    await onSave(cleanedVariant)
  }

  const isVariantValid = () => {
    // Check if name is valid (at least 2 characters)
    if (!variant?.name?.trim() || variant.name.trim().length < 2) {
      return false
    }

    // Check distributor codes - must have both name and code if either is filled
    const distributorCodes = variant?.distributor_codes || []
    const hasIncompleteCode = distributorCodes.some((code) => {
      const hasName = code.distributor_name?.trim()
      const hasCode = code.distributor_code?.trim()
      // Invalid if only one field is filled
      return (hasName && !hasCode) || (hasCode && !hasName)
    })

    if (hasIncompleteCode) {
      return false
    }

    // Check for duplicate distributor names (only for complete entries)
    const filledDistributorCodes = distributorCodes.filter(
      (code) => code.distributor_name?.trim() && code.distributor_code?.trim()
    )
    const distributorNames = filledDistributorCodes
      .map((code) => code.distributor_name?.trim().toLowerCase())
      .filter((name) => name)

    // Check if any name appears more than once
    const hasDuplicates = distributorNames.some(
      (name, index) => distributorNames.indexOf(name) !== index
    )

    return !hasDuplicates
  }

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="grid grid-cols-6 gap-y-4 gap-x-4">
        <div className="col-span-6">
          <Controller
            name="variant.name"
            control={control}
            rules={{
              required: 'Variant name is required',
              minLength: {
                value: 2,
                message: 'Variant name must be at least 2 characters',
              },
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                control={control as Control<any>}
                name="variant.name"
                type="text"
                label="Variant Name"
                placeholder="Variant Name"
                disabled={isSyndigoVariant}
                onChange={(value) => {
                  field.onChange(value)
                  trigger('variant.name')
                }}
              />
            )}
          />
        </div>

        <div className="col-span-6">
          <TextInput
            name="variant.description"
            control={control as Control<any>}
            type="text"
            label="Description"
            placeholder="Description"
            disabled={isSyndigoVariant}
          />
        </div>

        <div className="col-span-3">
          <TextInput
            name="variant.gtin"
            control={control as Control<any>}
            type="text"
            label="GTIN / UPC"
            placeholder="GTIN / UPC"
            disabled={isSyndigoVariant}
          />
        </div>
        <div className="col-span-3">
          <TextInput
            name="variant.sku"
            control={control as Control<any>}
            type="text"
            label="SKU"
            placeholder="SKU"
            disabled={isSyndigoVariant}
          />
        </div>

        <div className="col-span-3">
          <TextInput
            name="variant.avg_price"
            control={control as Control<any>}
            type="number"
            label={`Avg Price per ${reportingUnit ? getSingularUnit(reportingUnit) : 'unit'}`}
            placeholder="0.00"
            disabled={isSyndigoVariant}
            prefix="$"
            allowDecimals
          />
        </div>
        <div className="col-span-3">
          <TextInput
            name="variant.format_pack_size"
            control={control as Control<any>}
            type="text"
            label="Format / pack size"
            placeholder="Format / pack size"
            disabled={isSyndigoVariant}
          />
        </div>

        <div className="col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product
          </label>
          <Controller
            name="variant.product"
            control={control}
            render={({ field }) => (
              <ProductSelector
                value={field.value}
                onChange={field.onChange}
                placeholder="Select a product"
              />
            )}
          />
        </div>
        {enableBrandManagement && (
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand
            </label>
            <Controller
              name="variant.brand"
              control={control}
              render={({ field }) => (
                <BrandSelector
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select a brand"
                />
              )}
            />
          </div>
        )}

        <div className="col-span-6 mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Distributor Codes
            </h4>
            <button
              type="button"
              onClick={handleAddDistributorCode}
              className="flex items-center gap-2 text-sm text-brand hover:text-brand-pressed"
            >
              <CiSquarePlus size={13.5} />
              <span>Add Distributor Code</span>
            </button>
          </div>

          {distributorCodeFields.map((field, codeIndex) => {
            // Check if this distributor name is duplicated
            const currentName = variant?.distributor_codes?.[
              codeIndex
            ]?.distributor_name
              ?.trim()
              .toLowerCase()
            const isDuplicate =
              currentName &&
              variant?.distributor_codes?.some(
                (code, idx) =>
                  idx !== codeIndex &&
                  code.distributor_name?.trim().toLowerCase() === currentName
              )

            // Check for incomplete codes
            const hasName =
              variant?.distributor_codes?.[codeIndex]?.distributor_name?.trim()
            const hasCode =
              variant?.distributor_codes?.[codeIndex]?.distributor_code?.trim()
            const needsCode = hasName && !hasCode
            const needsName = hasCode && !hasName

            const nameError =
              errors?.variant?.distributor_codes?.[codeIndex]?.distributor_name
                ?.message
            const codeError =
              errors?.variant?.distributor_codes?.[codeIndex]?.distributor_code
                ?.message

            return (
              <div key={field.id} className="mb-4">
                <div className="flex gap-x-4">
                  <div className="flex-1">
                    <TextInput
                      name={`variant.distributor_codes.${codeIndex}.distributor_name`}
                      control={control as Control<any>}
                      type="text"
                      label="Distributor Name"
                      placeholder="Enter distributor name"
                      onChange={() => {
                        // Clear errors for all distributor codes when any changes
                        clearErrors(`variant.distributor_codes.${codeIndex}`)
                        variant?.distributor_codes?.forEach((_, idx) => {
                          trigger(
                            `variant.distributor_codes.${idx}.distributor_name`
                          )
                          trigger(
                            `variant.distributor_codes.${idx}.distributor_code`
                          )
                        })
                      }}
                    />
                    {(isDuplicate || nameError || needsName) && (
                      <div className="text-red-600 text-sm mt-1">
                        {nameError ||
                          (needsName
                            ? 'Distributor name is required'
                            : 'Distributor name must be unique')}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <TextInput
                      name={`variant.distributor_codes.${codeIndex}.distributor_code`}
                      control={control as Control<any>}
                      type="text"
                      label="Distributor Code"
                      placeholder="Enter distributor code"
                      onChange={() => {
                        // Clear errors when code changes
                        clearErrors(`variant.distributor_codes.${codeIndex}`)
                        trigger(
                          `variant.distributor_codes.${codeIndex}.distributor_code`
                        )
                        trigger(
                          `variant.distributor_codes.${codeIndex}.distributor_name`
                        )
                      }}
                    />
                    {(codeError || needsCode) && (
                      <div className="text-red-600 text-sm mt-1">
                        {codeError || 'Distributor code is required'}
                      </div>
                    )}
                  </div>
                  <div className="pt-3 pr-3 flex flex-col items-center justify-center">
                    <button
                      type="button"
                      onClick={() => removeDistributorCode(codeIndex)}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <MdClose />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <FbButton
          variant="secondary"
          onClick={onCancel}
          type="button"
          disabled={isSubmitting}
        >
          Cancel
        </FbButton>
        <FbButton type="submit" disabled={!isVariantValid() || isSubmitting}>
          {isSubmitting
            ? 'Saving...'
            : initialData
              ? 'Save Changes'
              : 'Create Variant'}
        </FbButton>
      </div>
    </form>
  )
}
